'use client';

import { ReactElement, useEffect } from 'react';
import { SessionData } from '@auth0/nextjs-auth0/types';
import Auth0Login from 'components/Auth0Login';
import LearnDropdownButton from 'components/app/header/LearnDropdownButton';
import Logo from 'components/Logo';
import AvatarButton from 'components/AvatarButton';
import MenuButton from './MenuButton';
import { usePathname } from 'next/navigation';
import Tooltip from 'components/Tooltip';
import HamburgerButton from './HamburgerButton';
import MobileNav from './MobileNav';
import AdminIcon from 'components/icons/AdminIcon';
import Link from 'next/link';
import { NAV_ITEMS } from './navItems';
import { useMobileNav } from 'contexts/MobileNavContext';

export default function MarketingNav({
  session,
  isAdmin = false,
}: {
  session: null | SessionData;
  isAdmin?: boolean;
}): ReactElement {
  const pathname = usePathname();
  const isOnAdminPage = pathname.startsWith('/admin');
  const { isMobileNavOpen: isOpen, setIsMobileNavOpen } = useMobileNav();
  const toggleMenu = () => {}; // Desktop menu buttons get this function for onClick prop
  const toggleMobileMenu = () => setIsMobileNavOpen(!isOpen);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileNavOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, setIsMobileNavOpen]);

  return (
    <header className="marketing-header">
      <aside className="agent-banner" inert={isOpen}>
        <div className="talk-to-agent">
          <div>
            Talk to a licensed insurance agent:{' '}
            <span>
              <a href="tel:+18337485302">(833) 748-5302</a>
            </span>{' '}
            TTY: 711
          </div>

          <Tooltip
            color="white"
            verticalAlign="bottom"
            subject="Business hours"
          >
            Business hours: Monday - Friday, 9 a.m. - 7 p.m. ET
          </Tooltip>
        </div>
      </aside>
      <nav>
        <div className="nav-sections-container">
          <div className="logo-container" inert={isOpen}>
            <Logo />
          </div>

          <ul className="nav-btns-container" inert={isOpen}>
            {NAV_ITEMS.map((item) => (
              <li
                key={item.slug}
                className={item.slug === pathname ? 'current-page' : ''}
              >
                {item.type === 'dropdown' ? (
                  <LearnDropdownButton
                    toggleMenu={toggleMenu}
                    pathname={pathname}
                  />
                ) : (
                  <MenuButton
                    toggleMenu={toggleMenu}
                    label={item.label}
                    slug={item.slug}
                    dataTestId={item.dataTestId}
                    isCurrentPage={item.slug === pathname}
                  />
                )}
              </li>
            ))}
          </ul>
          <div className="auth-container" inert={isOpen}>
            {!session && <Auth0Login session={session} />}
            {isAdmin && (
              <Link
                href="/admin"
                className={`admin-header-link${
                  isOnAdminPage ? ' admin-header-link--active' : ''
                }`}
                aria-current={isOnAdminPage ? 'page' : undefined}
              >
                <AdminIcon />
                Admin
              </Link>
            )}
            {session && <AvatarButton session={session} showDropdown={true} />}
          </div>

          <div className="hamburger-btn-container" inert={isOpen}>
            <HamburgerButton
              isOpen={isOpen}
              toggleMenu={toggleMobileMenu}
              ariaControls="mobile-nav-panel"
              aria-label="Open menu"
            />
          </div>

          <MobileNav
            session={session}
            isOpen={isOpen}
            toggleMenu={toggleMobileMenu}
            isAdmin={isAdmin}
            isOnAdminPage={isOnAdminPage}
          />
        </div>
      </nav>
    </header>
  );
}
